import {
  FunctionComponent,
  MutableRefObject,
  useState,
  useCallback,
  useEffect,
} from 'react'
import styled, { css } from 'styled-components'

import { RecommendedItem } from '@hooks/useRecommendations'
import {
  desktopCSS,
  tabletCSS,
  mobileCSS,
  landscapeOrientationCSS,
} from '@measures/responsive'

import CloseButton, { CloseButtonProps } from './CloseButton'
import ReplayButton, { ReplayButtonProps } from './ReplayButton'
import PlayButton, { PlayButtonProps } from './PlayButton'
import RecommendationsSwiper, {
  RecommendationsSwiperProps,
} from './RecommendationsSwiper'

import Portal from './Portal'
import usePlayerFullScreen from './usePlayerFullScreen'

interface VideoRecommendationsProps {
  hidden?: boolean
  data: RecommendedItem[]
  showReplayButton: boolean
  recommendationsTitle: string
  smallSize?: boolean
  isOverlay?: boolean
  videoPlayingStatus?: RecommendationsSwiperProps['videoPlayingStatus']

  swiperRef?: RecommendationsSwiperProps['swiperRef']
  playerRef: MutableRefObject<any>
  onClickClose: CloseButtonProps['onClick']
  onClickPlay: PlayButtonProps['onClick']
  onClickReplay: ReplayButtonProps['onClick']
  onClickItem: RecommendationsSwiperProps['onItemClick']
  onSliderMove: RecommendationsSwiperProps['onSliderMove']
  onNavigationChange: RecommendationsSwiperProps['onNavigationChange']
  onItemImpression: RecommendationsSwiperProps['onItemImpression']
}

interface PlayerDimensions {
  width: number
  height: number
}

const Wrapper = styled.div<
  Pick<VideoRecommendationsProps, 'smallSize' | 'hidden'>
>`
  ${({
    theme: {
      animation: {
        transition: { default: animationTransitionDefault },
      },
    },
    hidden,
  }) => {
    return css`
      display: flex;
      align-items: center;

      position: absolute;
      z-index: 6;
      width: 100%;
      height: 100%;

      background: radial-gradient(
        97.2% 149.96% at 100% 0%,
        #98021c 0%,
        #1c1e21 100%
      );

      transition: opacity ${animationTransitionDefault};
      visibility: visible;
      opacity: 1;

      ${hidden &&
      css`
        visibility: hidden;
        opacity: 0;
      `}
    `
  }}
`

const InnerWrapper = styled.div<Pick<VideoRecommendationsProps, 'smallSize'>>`
  width: 100%;
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
`

const RecommendationsTitle = styled.div<
  Pick<VideoRecommendationsProps, 'smallSize'>
>`
  ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_5 },
      },
      fonts: { goodOTCond },
    },
    smallSize,
  }) => {
    const landscapeStyles = landscapeOrientationCSS(css`
      font-size: 20px;

      @media (max-height: 320px) {
        font-size: 17px;
      }
    `)

    const mobileStyles = css`
      font-size: 16px;
      padding: 16px 16px 6px 16px;

      ${landscapeStyles}
    `

    const desktopStyles = smallSize
      ? mobileStyles
      : css`
          font-size: 24px;
          padding: 10px 20px 6px 32px;
        `

    const tabletStyles = smallSize
      ? mobileStyles
      : css`
          ${desktopStyles}
          ${landscapeStyles}
        `

    return css`
      font-family: ${goodOTCond};
      font-style: normal;
      font-weight: 800;
      line-height: 0.75;
      color: ${btvGrey_5};
      ${mobileCSS(mobileStyles)}
      ${desktopCSS(desktopStyles)} ${tabletCSS(tabletStyles)};
    `
  }}
`

const VideoRecommendations: FunctionComponent<VideoRecommendationsProps> = ({
  hidden = true,
  swiperRef,
  data,
  showReplayButton = false,
  recommendationsTitle,
  onClickClose,
  onClickReplay,
  onClickPlay,
  onClickItem,
  onSliderMove,
  onNavigationChange,
  onItemImpression,
  smallSize: smallSizeProp,
  isOverlay: isOverlayProp,
  playerRef,
}) => {
  /**
   * the initial state is set so that we do not
   * track more than two swiperItem recommendation impressions
   * before the first final sizes based on the player dimensions are set
   */
  const [swiperItemWidth, setSwiperItemWidth] = useState<number>(
    window.innerHeight * 0.2
  )
  const [swiperItemHeight, setSwiperItemHeight] = useState<number>(
    window.innerWidth * 0.5
  )
  const [isFullScreen] = usePlayerFullScreen({ playerRef })

  const isOverlay = isOverlayProp || isFullScreen
  const smallSize = !isOverlay && smallSizeProp

  const adjustSwiperItemDimensions = useCallback(
    (mainDimensionSize: number, ratio: number) => {
      setSwiperItemHeight(mainDimensionSize * ratio)
      setSwiperItemWidth((16 * mainDimensionSize * ratio) / 9)
    },
    []
  )

  const handlePlayerResizing = useCallback(() => {
    const isLandscape = window.matchMedia('(orientation: landscape)').matches
    const playerDimensions: PlayerDimensions = playerRef.current
      ?.player()
      ?.currentDimensions?.() || { width: 0, height: 0 }

    if (isOverlay) {
      if (isLandscape) {
        adjustSwiperItemDimensions(playerDimensions.height, 0.45)
      } else {
        adjustSwiperItemDimensions(playerDimensions.height, 0.2)
      }
    } else {
      adjustSwiperItemDimensions(playerDimensions.height, 0.5)
    }

    if (swiperRef?.current) {
      swiperRef?.current.update()
    }
  }, [playerRef, isOverlay, adjustSwiperItemDimensions, swiperRef])

  useEffect(() => {
    const player = playerRef?.current?.player?.()

    player.on('pause', handlePlayerResizing)
    player.on('playerresize', handlePlayerResizing)

    return () => {
      player.off('pause', handlePlayerResizing)
      player.off('playerresize', handlePlayerResizing)
    }
  }, [handlePlayerResizing, playerRef])

  return (
    <Wrapper hidden={hidden} smallSize={smallSize}>
      <CloseButton onClick={onClickClose} />
      <InnerWrapper smallSize={smallSize}>
        <RecommendationsTitle smallSize={smallSize}>
          {recommendationsTitle}
        </RecommendationsTitle>

        <RecommendationsSwiper
          hidden={hidden}
          smallSize={smallSize}
          data={data}
          swiperRef={swiperRef}
          onItemClick={onClickItem}
          onItemImpression={onItemImpression}
          onSliderMove={onSliderMove}
          onNavigationChange={onNavigationChange}
          swiperItemHeight={swiperItemHeight}
          swiperItemWidth={swiperItemWidth}
        />
        <ButtonsWrapper>
          {showReplayButton ? (
            <ReplayButton
              isOverlay={isOverlay}
              smallSize={smallSize}
              onClick={onClickReplay}
            />
          ) : (
            <PlayButton
              isOverlay={isOverlay}
              smallSize={smallSize}
              onClick={onClickPlay}
            />
          )}
        </ButtonsWrapper>
      </InnerWrapper>
    </Wrapper>
  )
}

const VideoRecommendationsPortal: FunctionComponent<
  Omit<VideoRecommendationsProps, 'swiperItemWidth' | 'swiperItemHeight'> & {
    playerRef: MutableRefObject<any>
  }
> = (props) => {
  const parentNode = props.playerRef.current?.player()?.el_ as HTMLElement

  return (
    <Portal parentNode={parentNode} hidden={props.hidden}>
      <VideoRecommendations {...props} />
    </Portal>
  )
}

export default VideoRecommendationsPortal
